import { useEffect, useRef, useState } from "react";
import { overdueLevel, overdueThreshold, type OverdueLevel } from "./health";

/**
 * Keeps the last few overdue readings so the panel can tell a churning number
 * apart from a growing one. Deliberately in memory and not persisted: a
 * backlog from yesterday is not evidence about now, and reloading the page is
 * how an operator asks for a fresh opinion.
 *
 * Keyed on `asOf` -- the server's own clock -- so a re-render, a refocus or a
 * refetch that returns the identical reading does not count as a new one and
 * cannot manufacture a sustained backlog out of one measurement.
 */
export function useOverdueSignal(
  overdue: number | undefined,
  asOf: Date | undefined,
  alertsActive: number,
): { level: OverdueLevel; threshold: number; readings: number[] } {
  const seen = useRef<string | null>(null);
  const [readings, setReadings] = useState<number[]>([]);

  useEffect(() => {
    if (overdue === undefined || asOf === undefined) return;
    const stamp = asOf.toISOString();
    if (seen.current === stamp) return;
    seen.current = stamp;
    setReadings((previous) => [...previous, overdue].slice(-8));
  }, [overdue, asOf]);

  const threshold = overdueThreshold(alertsActive);
  return { level: overdueLevel(readings, threshold), threshold, readings };
}
